import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ProfilesListPage.css';
import API_BASE_URL from '../config';

const ProfilesListPage = () => {
  const [profilesList, setProfilesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  // Helper function to get file type from URL
  const getFileType = (url) => {
    if (!url) return null;
    const fileName = url.split('/').pop().split('?')[0].toLowerCase();
    if (fileName.endsWith('.pdf')) return 'pdf';
    if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg')) return 'image';
    if (fileName.endsWith('.png')) return 'image';
    if (fileName.endsWith('.gif')) return 'image';
    if (fileName.endsWith('.doc') || fileName.endsWith('.docx')) return 'document';
    return 'file';
  };

  // Helper function to get resume icon based on file type
  const getResumeIcon = (url) => {
    const fileType = getFileType(url);
    if (fileType === 'pdf') return '📄';
    if (fileType === 'image') return '🖼️';
    if (fileType === 'document') return '📝';
    return '📎';
  };

  useEffect(() => {
    // Check if user is logged in and is recruiter
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (!token || !storedUser) {
      alert('Please login to access profiles');
      navigate('/login', { state: { from: '/profiles' } });
      return;
    }

    try {
      const userData = JSON.parse(storedUser);
      const userType = String(userData?.userType || '').toLowerCase();
      const canViewProfiles =
        userType === 'recruiter' || userType === 'producer' || userType === 'investor';

      if (!canViewProfiles) {
        alert('Only recruiters/producers can view profiles. Talent users can create/edit their profile.');
        navigate('/profile');
        return;
      }

      setUser(userData);
      fetchAllProfiles();
    } catch (err) {
      console.error('Error parsing user data:', err);
      navigate('/login');
    }
  }, [navigate]);

  // Fetch all profiles created by users (talent)
  const fetchAllProfiles = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/profiles`);

      if (res.ok) {
        const data = await res.json();
        setProfilesList(data.profiles || []);
      } else {
        const data = await res.json().catch(() => ({}));
        console.error('Failed to fetch profiles:', data.error);
        alert('Failed to load profiles: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error fetching profiles:', err);
      alert('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="marketplace-container">
        <div style={{ textAlign: 'center', padding: '3rem' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="marketplace-container">
      {/* Header */}
      <header className="marketplace-header">
        <div className="logo-left">
          <img src="/p3.jpg" alt="Cinepreneur Logo" className="cinepreneur-logo" />
        </div>
        <div className="header-content">
          <h1 className="marketplace-title">CREATOR'S ARCH</h1>
          <p className="marketplace-subtitle">EMPOWERING CREATORS, PROFESSIONALS</p>
        </div>
        <div className="logo-right">
          <img src="/p1.jpg" alt="MeeSchool Logo" className="meeschool-logo" />
        </div>
      </header>

      <div className="marketplace-body">
        {/* Left Navigation Sidebar */}
        <nav className="sidebar-nav">
          <div className="nav-item" onClick={() => navigate('/')}>HOME</div>
          <div className="nav-item" onClick={() => navigate('/certified-professional')}>CERTIFIED PROFESSIONAL</div>
          <div className="nav-item active">ALL PROFILES</div>
          <div className="nav-item">
            SOURCING
            <div className="nav-subitem">TALENT/EQUIPMENT</div>
          </div>
          <div className="nav-item" onClick={() => navigate('/producer-pitches')}>PITCHING</div>
          <div className="nav-item">
            VALIDATE
            <div className="nav-subitem">PROOF OF CONCEPTS</div>
          </div>
          <div className="nav-item" onClick={() => navigate('/funding-projects')}>FUNDING ZONE</div>
        </nav>

        {/* Main Content Area - Profiles List */}
        <main className="main-content">
          <h2 style={{ marginBottom: '20px', fontSize: '24px', color: '#333' }}>PROFILES</h2>
          
          {profilesList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              <p>No profiles found.</p>
            </div>
          ) : (
            <div className="profiles-grid">
              {profilesList.map((profile, index) => (
                <div key={profile.Id || index} className="profile-card">
                  {profile.ProfileImageUrl && (
                    <div className="profile-image-container">
                      <img 
                        src={profile.ProfileImageUrl} 
                        alt={`${profile.Name || 'Profile'}`}
                        className="profile-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <div className="profile-info">
                    <h3 className="profile-name">{profile.Name || 'N/A'}</h3>
                    <p className="profile-email">{profile.Email || profile.UserEmail || 'N/A'}</p>
                    {profile.Company && <p className="profile-detail"><strong>Company:</strong> {profile.Company}</p>}
                    {(profile.Occupation || profile.Designation) && <p className="profile-detail"><strong>Occupation / Role:</strong> {profile.Occupation || profile.Designation}</p>}
                    {profile.Skill && <p className="profile-detail"><strong>Skill:</strong> {profile.Skill}</p>}
                    {profile.CityLocation && <p className="profile-detail"><strong>Location:</strong> {profile.CityLocation}</p>}
                    {profile.Education && <p className="profile-detail"><strong>Education:</strong> {profile.Education}</p>}
                    {profile.Phone && <p className="profile-detail"><strong>Phone:</strong> {profile.Phone}</p>}
                    {(profile.LinkedInUrl || profile.InstagramUrl || profile.TwitterUrl || profile.YouTubeUrl || profile.PortfolioUrl) && (
                      <div className="profile-detail profile-social-links">
                        <strong>Links:</strong>{' '}
                        {profile.LinkedInUrl && <a href={profile.LinkedInUrl} target="_blank" rel="noopener noreferrer" title="LinkedIn">LinkedIn</a>}
                        {profile.LinkedInUrl && (profile.InstagramUrl || profile.TwitterUrl || profile.YouTubeUrl || profile.PortfolioUrl) && ' · '}
                        {profile.InstagramUrl && <a href={profile.InstagramUrl} target="_blank" rel="noopener noreferrer" title="Instagram">Instagram</a>}
                        {profile.InstagramUrl && (profile.TwitterUrl || profile.YouTubeUrl || profile.PortfolioUrl) && ' · '}
                        {profile.TwitterUrl && <a href={profile.TwitterUrl} target="_blank" rel="noopener noreferrer" title="Twitter/X">Twitter</a>}
                        {profile.TwitterUrl && (profile.YouTubeUrl || profile.PortfolioUrl) && ' · '}
                        {profile.YouTubeUrl && <a href={profile.YouTubeUrl} target="_blank" rel="noopener noreferrer" title="YouTube">YouTube</a>}
                        {profile.YouTubeUrl && profile.PortfolioUrl && ' · '}
                        {profile.PortfolioUrl && <a href={profile.PortfolioUrl} target="_blank" rel="noopener noreferrer" title="Portfolio">Portfolio</a>}
                      </div>
                    )}
                    {profile.CareerAspirations && <p className="profile-detail profile-aspirations"><strong>Aspirations:</strong> {profile.CareerAspirations}</p>}
                    {profile.ResumeUrl && (
                      <a 
                        href={profile.ResumeUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="resume-link"
                      >
                        {getResumeIcon(profile.ResumeUrl)} View Resume
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Footer */}
      <footer className="marketplace-footer">
        <p>© MEESCHOOL, 2026</p>
      </footer>
    </div>
  );
};

export default ProfilesListPage;

